import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  MessageSquare,
  Tag,
  BarChart3,
  Store,
  DollarSign,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useConversationUnread } from '../../hooks/useConversationUnread';
import { usePendingOfferCount } from '../../hooks/usePendingOfferCount';
import { SellerNotification } from '../../types/notifications';

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const { seller, token } = useAuth();
  const { notifications, unreadSummary } = useNotifications();
  const { unreadCount: conversationUnread } = useConversationUnread(!!token);
  const { pendingCount: pendingOffers } = usePendingOfferCount(!!token);

  const notificationCounts = useMemo(() => {
    const unreadOrders = notifications.filter(
      (n: SellerNotification) => n.type === 'order' && !n.isRead
    ).length;
    const unreadPayments = notifications.filter(
      (n: SellerNotification) => n.type === 'payment' && !n.isRead
    ).length;
    const unreadOfferNotifications = unreadSummary.offers;
    const offersBadge = Math.max(pendingOffers, unreadOfferNotifications);
    return { orders: unreadOrders, payments: unreadPayments, offers: offersBadge };
  }, [notifications, unreadSummary.offers, pendingOffers]);

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: t('menu.dashboard'),
      path: '/',
      notificationCount: 0,
    },
    {
      icon: Package,
      label: t('menu.products'),
      path: '/products',
      notificationCount: 0,
    },
    {
      icon: ShoppingCart,
      label: t('menu.orders'),
      path: '/orders',
      notificationCount: notificationCounts.orders,
    },
    {
      icon: MessageSquare,
      label: t('menu.messages'),
      path: '/conversations',
      notificationCount: conversationUnread,
    },
    {
      icon: Tag,
      label: t('menu.offers'),
      path: '/offers',
      notificationCount: notificationCounts.offers,
    },
    {
      icon: BarChart3,
      label: t('menu.stats'),
      path: '/stats',
      notificationCount: 0,
    },
    {
      icon: DollarSign,
      label: t('menu.payments'),
      path: '/payments',
      notificationCount: notificationCounts.payments,
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="bg-gray-800 w-64 shadow-lg flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 md:p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Store className="text-custom-secondary hidden md:block" size={28} />
            <Store className="text-custom-secondary md:hidden" size={24} />
            <h1 className="text-lg md:text-2xl font-bold text-white">Seller Portal</h1>
          </div>
          {/* Close button for mobile */}
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X size={24} />
            </button>
          )}
        </div>
        {seller && (
          <p className="text-xs md:text-sm text-gray-400 mt-1 truncate">{seller.name}</p>
        )}
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-2 md:p-4 space-y-1 md:space-y-2 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`flex items-center justify-between px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors ${
                active
                  ? 'bg-custom-primary text-white shadow-sm'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-2 md:space-x-3">
                <Icon size={18} className="md:w-5 md:h-5" />
                <span className="font-medium text-sm md:text-base">{item.label}</span>
              </div>
              {item.notificationCount > 0 && (
                <span className={`text-xs font-bold px-1.5 md:px-2 py-0.5 rounded-full ${
                  active ? 'bg-white text-custom-primary' : 'bg-red-500 text-white'
                }`}>
                  {item.notificationCount > 9 ? '9+' : item.notificationCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;

